import {defineStore} from "pinia";
import Recorder from 'recorder-js';
import {computed, ref} from "vue";
import {enableKeepAwake, disableKeepAwake} from "@src/services/keepAwake";
import {Toast} from "@src/plugins/toast";
import {requestAudioFocus, releaseAudioFocus} from "@src/services/audioFocus";

export const useAudioStore = defineStore('audio', () => {
    const isRecording = ref<boolean>(false)
    const isPaused = ref<boolean>(false)
    const duration = ref<number>(0)
    const recorder = ref<Recorder | null>(null)
    const format = ref<string>('mp3')
    const intervalId = ref<number | null>(null)
    const mediaStream = ref<MediaStream | null>(null)
    const audioContext = ref<AudioContext | null>(null)
    const isStarting = ref<boolean>(false)


    const recordedTime = computed(() => {
        const mins = Math.floor(duration.value / 60)
        const secs = duration.value % 60
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    })


    function micFailed(): void {
        isRecording.value = false
        stopTimer()
        releaseMediaStream()
    }

    function startTimer(): void {
        if (intervalId.value) {
            clearInterval(intervalId.value)
        }

        duration.value = 0
        intervalId.value = setInterval(() => {
            duration.value++
        }, 1000) as any
    }

    function stopTimer(): void {
        if (intervalId.value) {
            clearInterval(intervalId.value)
            intervalId.value = null
        }
        duration.value = 0
    }

    function releaseMediaStream(): void {
        if (mediaStream.value) {
            mediaStream.value.getTracks().forEach(track => track.stop())
            mediaStream.value = null
        }
    }

    function stopRecorder(): void {
        try {
            recorder.value?.stop()
        } catch {
        }

        recorder.value = null

        if (mediaStream.value) {
            mediaStream.value.getTracks().forEach(t => {
                t.stop()
                t.enabled = false
            })
            mediaStream.value = null
        }

        if (audioContext.value) {
            audioContext.value.close()
            audioContext.value = null
        }

        stopTimer()
        isRecording.value = false
        isPaused.value = false
        disableKeepAwake()
        releaseAudioFocus()
    }


    // No Android, o fluxo nativo de permissão RECORD_AUDIO pode tirar o foco
    // da WebView por um instante (mitigado no lado nativo, em
    // MicAwareWebChromeClient.java) e, mesmo com a permissão já concedida, o
    // HAL de áudio às vezes ainda não está pronto logo após a primeira
    // concessão no processo, retornando NotReadableError. Os retries com
    // backoff abaixo são a rede de segurança para os dois casos.
    const RETRY_DELAYS_MS = [300, 700]

    async function getAudioStream(): Promise<MediaStream> {
        for (const delay of RETRY_DELAYS_MS) {
            try {
                return await navigator.mediaDevices.getUserMedia({audio: true})
            } catch (e) {
                if (!(e instanceof DOMException)) throw e
                await new Promise(resolve => setTimeout(resolve, delay))
            }
        }
        return await navigator.mediaDevices.getUserMedia({audio: true})
    }

    async function startRecorder(): Promise<void> {
        if (isRecording.value || isStarting.value) return
        isStarting.value = true

        try {
            const stream = await getAudioStream()
            mediaStream.value = stream

            audioContext.value = new (window.AudioContext || (window as any).webkitAudioContext)()
            if (audioContext.value.state === 'suspended') {
                await audioContext.value.resume()
            }
            recorder.value = new Recorder(audioContext.value, {
                // @ts-ignore bitRate is a valid option for this Recorder implementation
                bitRate: 128,
                sampleRate: 44100,
                format: format.value,
            })

            recorder.value.init(stream)
            recorder.value.start()

            isRecording.value = true
            isPaused.value = false
            startTimer()
            enableKeepAwake()
            requestAudioFocus()
        } catch (e) {
            if (e instanceof DOMException) {
                console.error(`[audio] getUserMedia failed: ${e.name} - ${e.message}`)
                if (e.name === 'NotAllowedError') {
                    Toast.fire(
                        "Permissão de microfone negada",
                        "Ative o microfone nas permissões do app, nas configurações do seu celular.",
                        "error"
                    )
                }
            } else {
                console.error(e)
            }
            micFailed()
        } finally {
            isStarting.value = false
        }
    }


    return {
        isRecording,
        isPaused,
        isStarting,
        duration,
        format,
        recorder,
        recordedTime,
        startRecorder,
        stopRecorder,
    }
})